import { logger } from '../../utils/logger';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import AdminNavbar from '../../components/AdminNavbar';
import axios from '../../api/axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Eye, X, CheckCircle2, Banknote, Smartphone, Clock, ListFilter, AlertCircle, ShoppingBag, Truck, Check, Archive, MapPin, User, Phone, Trash2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';


const AdminOrders = () => {
  const { admin } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [viewImage, setViewImage] = useState(null);

  useEffect(() => {
    if (!admin) {
      navigate('/admin/login');
      return;
    }
    fetchOrders();
  }, [admin, navigate]);

  const fetchOrders = async () => {
    try {
      const response = await axios.get('/orders');
      setOrders(response.data);
      setLoading(false);
    } catch (error) {
      logger.error('Error fetching orders:', error);
      setLoading(false);
    }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      await axios.patch(`/orders/${orderId}/status`, { status: newStatus });
      fetchOrders();
      const orderIdToCheck = selectedOrder?.id || selectedOrder?._id;
      if (selectedOrder && orderIdToCheck === orderId) {
        setSelectedOrder({ ...selectedOrder, status: newStatus });
      }
    } catch (error) {
      logger.error('Error updating order status:', error);
      alert('Failed to update order status');
    }
  };


  const handleDeleteOrder = async (orderId) => {
    if (window.confirm('Are you sure you want to PERMANENTLY delete this order? This action cannot be undone.')) {
      try {
        await axios.delete(`/orders/${orderId}`);
        setOrders(orders.filter(o => (o.id || o._id) !== orderId));
        if (selectedOrder && (selectedOrder.id || selectedOrder._id) === orderId) {
          setSelectedOrder(null);
        }
        // Optional: Show toast
      } catch (error) {
        logger.error('Error deleting order:', error);
        alert('Failed to delete order');
      }
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-amber-500/20 text-amber-500 border-amber-500/50',
      accepted: 'bg-blue-500/20 text-blue-500 border-blue-500/50',
      preparing: 'bg-purple-500/20 text-purple-500 border-purple-500/50',
      ready: 'bg-green-500/20 text-green-500 border-green-500/50',
      completed: 'bg-gray-500/20 text-gray-500 border-gray-500/50',
      cancelled: 'bg-red-500/20 text-red-500 border-red-500/50'
    };
    return colors[status] || 'bg-gray-500/20 text-gray-500';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return Clock;
      case 'accepted': return CheckCircle2;
      case 'preparing': return ShoppingBag;
      case 'ready': return Truck;
      case 'completed': return Check;
      case 'cancelled': return X;
      default: return Clock;
    }
  };

  const filteredOrders = statusFilter === 'all'
    ? orders
    : orders.filter(order => order.status === statusFilter);

  if (!admin) return null;

  return (
    <div className="min-h-screen bg-background pb-20">
      <AdminNavbar />

      <div className="container mx-auto px-6 py-8">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
          <div>
            <h1 className="text-4xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-gold-400 to-gold-200">
              Order Management
            </h1>
            <p className="text-gray-400 mt-2">Track and manage customer orders in real-time</p>
          </div>

          <div className="flex items-center gap-2 bg-black/40 p-1.5 rounded-lg border border-white/10 overflow-x-auto max-w-full">
            {['all', 'pending', 'accepted', 'preparing', 'ready', 'completed', 'cancelled'].map(status => (
              <button
                key={status}
                onClick={() => setStatusFilter(status)}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-all whitespace-nowrap ${statusFilter === status
                  ? 'bg-gold-500 text-black shadow-lg shadow-gold-500/20'
                  : 'text-gray-400 hover:text-white hover:bg-white/5'
                  }`}
              >
                {status.charAt(0).toUpperCase() + status.slice(1)}
                <span className="ml-2 text-xs opacity-60">
                  ({status === 'all' ? orders.length : orders.filter(o => o.status === status).length})
                </span>
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gold-500"></div>
          </div>
        ) : filteredOrders.length === 0 ? (
          <div className="bg-black/20 rounded-xl p-12 text-center border border-white/5">
            <Archive className="w-16 h-16 text-gray-700 mx-auto mb-4" />
            <p className="text-gray-400 text-xl">No orders found</p>
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-4"
          >
            <AnimatePresence>
              {filteredOrders.map((order, index) => {
                const StatusIcon = getStatusIcon(order.status);
                return (
                  <motion.div
                    key={order.id || order._id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <Card className="bg-gray-900/40 backdrop-blur-sm border-gray-800 hover:border-gold-500/30 transition-all duration-300">
                      <CardContent className="p-6">
                        <div className="flex flex-col lg:flex-row justify-between lg:items-center gap-6">
                          {/* Order info & Customer */}
                          <div className="flex flex-col md:flex-row gap-6">
                            <div className="flex flex-col gap-1 min-w-[150px]">
                              <h3 className="text-2xl font-serif font-bold text-gold-200">#{order.orderNumber}</h3>
                              <p className="text-sm text-gray-500 flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {new Date(order.createdAt).toLocaleString('en-PK')}
                              </p>
                              <div className={`flex items-center gap-2 mt-2 px-3 py-1 rounded-full text-xs font-bold border w-fit ${getStatusColor(order.status)}`}>
                                <StatusIcon className="w-3 h-3" />
                                <span className="uppercase">{order.status}</span>
                              </div>
                            </div>

                            <div className="flex flex-col gap-2 border-l border-white/5 pl-6">
                              <span className="text-xs text-gold-500 font-bold uppercase tracking-wider">Customer</span>
                              <div className="space-y-1">
                                <div className="flex items-center gap-2 text-white font-medium">
                                  <User className="w-4 h-4 text-gray-500" /> {order.customerName}
                                </div>
                                <div className="flex items-center gap-2 text-gray-400 text-sm">
                                  <Phone className="w-4 h-4 text-gray-500" /> {order.customerPhone}
                                </div>
                                <div className="flex items-center gap-2 text-gray-400 text-sm max-w-sm">
                                  <MapPin className="w-4 h-4 text-gray-500 shrink-0" /> <span className="line-clamp-1">{order.customerAddress}</span>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Items & Total */}
                          <div className="flex flex-col md:flex-row gap-8 lg:items-center border-l border-white/5 pl-6 md:pl-0 lg:pl-6">
                            <div className="flex flex-col gap-2">
                              <span className="text-xs text-gold-500 font-bold uppercase tracking-wider">Order Items</span>
                              <p className="text-white text-lg">{order.items?.length || 0} <span className="text-gray-500 text-sm">item(s)</span></p>
                              <div className="flex items-center gap-2">
                                {order.paymentMethod === 'easypaisa' ? <Smartphone className="w-4 h-4 text-pink-500" /> : <Banknote className="w-4 h-4 text-green-500" />}
                                <span className="text-sm text-gray-300 capitalize">{order.paymentMethod === 'cod' ? 'Cash On Delivery' : order.paymentMethod}</span>
                              </div>
                            </div>

                            <div className="flex flex-col md:items-end gap-1">
                              <span className="text-xs text-gold-500 font-bold uppercase tracking-wider">Total Amount</span>
                              <span className="text-3xl font-bold text-white">Rs. {order.totalAmount?.toLocaleString() || '0'}</span>
                            </div>
                          </div>

                          {/* Actions */}
                          <div className="flex flex-wrap lg:flex-col xl:flex-row gap-2 mt-4 lg:mt-0 lg:ml-auto">
                            <Button
                              onClick={() => setSelectedOrder(order)}
                              className="bg-blue-600/20 text-blue-400 hover:bg-blue-600 hover:text-white"
                            >
                              <Eye className="w-4 h-4 mr-2" /> Details
                            </Button>

                            {['completed', 'cancelled'].includes(order.status) && (
                              <Button
                                variant="destructive"
                                onClick={() => handleDeleteOrder(order.id || order._id)}
                                className="bg-red-500/10 text-red-500 hover:bg-red-500 hover:text-white border border-red-500/20"
                                title="Delete Order"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}

                            {order.status === 'pending' && (
                              <Button
                                onClick={() => updateOrderStatus(order.id || order._id, 'accepted')}
                                className="bg-green-600 text-white hover:bg-green-700 font-bold"
                              >
                                Accept Order
                              </Button>
                            )}

                            {order.status === 'accepted' && (
                              <Button
                                onClick={() => updateOrderStatus(order.id || order._id, 'preparing')}
                                className="bg-purple-600 text-white hover:bg-purple-700 font-bold"
                              >
                                Start Preparing
                              </Button>
                            )}

                            {order.status === 'preparing' && (
                              <Button
                                onClick={() => updateOrderStatus(order.id || order._id, 'ready')}
                                className="bg-green-600 text-white hover:bg-green-700 font-bold"
                              >
                                Mark Ready
                              </Button>
                            )}

                            {order.status === 'ready' && (
                              <Button
                                onClick={() => updateOrderStatus(order.id || order._id, 'completed')}
                                className="bg-gray-600 text-white hover:bg-gray-700 font-bold"
                              >
                                Complete
                              </Button>
                            )}

                            {!['completed', 'cancelled'].includes(order.status) && (
                              <Button
                                variant="outline"
                                onClick={() => {
                                  if (window.confirm('Are you sure you want to cancel this order?')) {
                                    updateOrderStatus(order.id || order._id, 'cancelled');
                                  }
                                }}
                                className="border-red-500/30 text-red-500 hover:bg-red-500 hover:text-white"
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                )
              })}
            </AnimatePresence>
          </motion.div>
        )}
      </div>

      {/* Order Details Modal */}
      <AnimatePresence>
        {selectedOrder && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedOrder(null)}
              className="absolute inset-0 bg-black/80 backdrop-blur-sm"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative bg-gray-900 rounded-xl max-w-4xl w-full border border-gold-500/30 max-h-[90vh] overflow-hidden flex flex-col shadow-2xl"
            >
              <div className="flex justify-between items-center p-6 border-b border-white/10 bg-black/40">
                <div>
                  <h2 className="text-3xl font-serif font-bold text-gold-400">Order #{selectedOrder.orderNumber}</h2>
                  <p className="text-gray-400 text-sm mt-1">
                    Placed on {new Date(selectedOrder.createdAt).toLocaleString('en-PK')}
                  </p>
                </div>
                <Button variant="ghost" size="icon" onClick={() => setSelectedOrder(null)} className="text-gray-400 hover:text-white">
                  <X className="w-6 h-6" />
                </Button>
              </div>

              <div className="flex-1 overflow-y-auto p-6 custom-scrollbar">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {/* Left Column: Customer & Status */}
                  <div className="space-y-6">
                    <Card className="bg-black/20 border-white/10">
                      <CardContent className="p-4">
                        <h3 className="text-gold-500 font-bold text-sm uppercase mb-4 flex items-center gap-2">
                          <User className="w-4 h-4" /> Customer Details
                        </h3>
                        <div className="space-y-3">
                          <div className="flex justify-between">
                            <span className="text-gray-500">Name</span>
                            <span className="text-white font-medium">{selectedOrder.customerName}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-500">Phone</span>
                            <span className="text-white font-medium">{selectedOrder.customerPhone}</span>
                          </div>
                          <div className="space-y-1">
                            <span className="text-gray-500 block">Delivery Address</span>
                            <p className="text-white bg-white/5 p-3 rounded-md text-sm leading-relaxed">
                              {selectedOrder.customerAddress}
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="bg-black/20 border-white/10">
                      <CardContent className="p-4">
                        <h3 className="text-gold-500 font-bold text-sm uppercase mb-4 flex items-center gap-2">
                          <ListFilter className="w-4 h-4" /> Order Info
                        </h3>
                        <div className="space-y-3">
                          <div className="flex justify-between items-center">
                            <span className="text-gray-500">Current Status</span>
                            <span className={`px-3 py-1 rounded-full text-xs font-bold border capitalize ${getStatusColor(selectedOrder.status)}`}>
                              {selectedOrder.status}
                            </span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-gray-500">Payment Method</span>
                            <div className="flex items-center gap-2 text-white">
                              {selectedOrder.paymentMethod === 'easypaisa' ? <Smartphone className="w-4 h-4 text-pink-400" /> : <Banknote className="w-4 h-4 text-green-400" />}
                              <span className="capitalize">{selectedOrder.paymentMethod === 'cod' ? 'Cash On Delivery' : 'Easypaisa'}</span>
                            </div>
                          </div>

                          {selectedOrder.paymentMethod === 'easypaisa' && (
                            <div className="mt-4 pt-4 border-t border-white/10">
                              <div className="flex justify-between items-center mb-2">
                                <span className="text-gray-400 text-sm">Payment Status</span>
                                {selectedOrder.paymentVerified ? (
                                  <span className="text-green-400 text-xs font-bold flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> VERIFIED</span>
                                ) : (
                                  <span className="text-amber-400 text-xs font-bold flex items-center gap-1"><AlertCircle className="w-3 h-3" /> PENDING VERIFICATION</span>
                                )}
                              </div>
                              {selectedOrder.paymentScreenshot && (
                                <div className="mt-2 group relative rounded-lg overflow-hidden border border-white/10 cursor-pointer" onClick={() => setViewImage(selectedOrder.paymentScreenshot)}>
                                  <img src={selectedOrder.paymentScreenshot} alt="Proof" className="w-full h-32 object-cover opacity-80 group-hover:opacity-100 transition-opacity" />
                                  <div className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <span className="text-white text-xs font-bold">View Screenshot</span>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>

                    {selectedOrder.notes && (
                      <Card className="bg-amber-500/5 border-amber-500/20">
                        <CardContent className="p-4">
                          <h3 className="text-amber-500 font-bold text-sm uppercase mb-2">Special Instructions</h3>
                          <p className="text-gray-300 text-sm italic">"{selectedOrder.notes}"</p>
                        </CardContent>
                      </Card>
                    )}
                  </div>

                  {/* Right Column: Items & Total */}
                  <div className="flex flex-col h-full">
                    <Card className="bg-black/20 border-white/10 flex-1 flex flex-col">
                      <CardContent className="p-0 flex-1 flex flex-col">
                        <div className="p-4 border-b border-white/10 bg-white/5">
                          <h3 className="text-gold-500 font-bold text-sm uppercase flex items-center gap-2">
                            <ShoppingBag className="w-4 h-4" /> Order Items
                          </h3>
                        </div>
                        <div className="max-h-[300px] overflow-y-auto p-4 space-y-4 custom-scrollbar flex-1">
                          {(selectedOrder.items || selectedOrder.items_json || []).map((item, i) => (
                            <div key={i} className="flex gap-4">
                              <div className="w-12 h-12 bg-gray-800 rounded-lg flex items-center justify-center text-gold-500 font-bold border border-white/5 shrink-0">
                                x{item.quantity}
                              </div>
                              <div className="flex-1">
                                <div className="flex justify-between items-start">
                                  <h4 className="text-white font-bold">{item.name}</h4>
                                  <span className="text-gold-400 font-bold">Rs. {item.itemTotal.toLocaleString()}</span>
                                </div>

                                {item.selectedOptions && item.selectedOptions.length > 0 && (
                                  <div className="mt-1 space-y-1">
                                    {item.selectedOptions.map((opt, idx) => (
                                      <div key={idx} className="text-xs text-gray-400 flex justify-between">
                                        <span>+ {opt.name}</span>
                                        <span>Rs. {opt.additionalPrice}</span>
                                      </div>
                                    ))}
                                  </div>
                                )}

                                {item.selectedSettings && Object.keys(item.selectedSettings).length > 0 && (
                                  <div className="mt-2 flex flex-wrap gap-1">
                                    {Object.entries(item.selectedSettings).map(([k, v], idx) => (
                                      <span key={idx} className="text-[10px] bg-white/10 text-gray-300 px-2 py-0.5 rounded uppercase">
                                        {k}: {v}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>

                        <div className="p-6 bg-gold-900/10 border-t border-gold-500/20 mt-auto">
                          <div className="flex justify-between items-center text-sm text-gray-400 mb-2">
                            <span>Subtotal</span>
                            <span>Rs. {selectedOrder.totalAmount.toLocaleString()}</span>
                          </div>
                          <div className="flex justify-between items-center text-sm text-gray-400 mb-4">
                            <span>Delivery Fee</span>
                            <span className="italic">Calculated by Restaurant</span>
                          </div>
                          <div className="flex justify-between items-center pt-4 border-t border-white/10">
                            <span className="text-xl font-bold text-white">Total Amount</span>
                            <span className="text-3xl font-bold text-gold-500">Rs. {selectedOrder.totalAmount.toLocaleString()}</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              </div>

              <div className="p-4 border-t border-white/10 bg-black/40 flex justify-between items-center">
                {['completed', 'cancelled'].includes(selectedOrder.status) ? (
                  <Button
                    variant="destructive"
                    onClick={() => handleDeleteOrder(selectedOrder.id || selectedOrder._id)}
                    className="bg-red-500/10 text-red-500 hover:bg-red-500 hover:text-white border border-red-500/20"
                  >
                    <Trash2 className="w-4 h-4 mr-2" /> Delete Order
                  </Button>
                ) : <div></div>}

                <Button onClick={() => setSelectedOrder(null)} className="w-full md:w-auto bg-gray-800 hover:bg-gray-700 text-white ml-auto">
                  Close Details
                </Button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Image Lightbox */}
      <AnimatePresence>
        {viewImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setViewImage(null)}
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/95 backdrop-blur-xl p-4"
          >
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setViewImage(null)}
              className="absolute top-4 right-4 text-white hover:bg-white/10 z-50 rounded-full w-12 h-12"
            >
              <X className="w-8 h-8" />
            </Button>

            <motion.img
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              src={viewImage}
              alt="Payment Proof Fullscreen"
              className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl border border-white/10"
              onClick={(e) => e.stopPropagation()}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminOrders;


